import { useTenantStore } from '../stores/useTenantStore';
import { fetchWithAuth } from '../utils/fetchWithAuth';

// Types pour l'API chat-backend
export interface ChatConversation {
  id: string;
  title: string;
  model_id: string;
  model_name: string;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface CreateConversationRequest {
  title: string;
  model_id: string;
  model_name: string;
}

export interface CreateMessageRequest {
  role: 'user' | 'assistant';
  content: string;
  metadata?: Record<string, any>;
}

export interface ConversationsResponse {
  conversations: ChatConversation[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface MessagesResponse {
  messages: ChatMessage[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

class ChatHistoryService {
  private getBaseUrl(): string {
    // Récupérer l'URL depuis le tenant manager
    const { getChatBackendUrl } = useTenantStore.getState();
    const chatBackendUrl = getChatBackendUrl();

    if (!chatBackendUrl) {
      throw new Error('Chat backend URL not configured in tenant manager');
    }

    return chatBackendUrl;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.getBaseUrl()}${endpoint}`;

    const response = await fetchWithAuth(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorJson.message || errorMessage;
      } catch {
        // Si ce n'est pas du JSON, utiliser le texte brut
        if (errorText) {
          errorMessage = errorText;
        }
      }

      throw new Error(`Chat Backend API Error: ${errorMessage}`);
    }

    const responseText = await response.text();
    if (!responseText) {
      return {} as T;
    }

    try {
      return JSON.parse(responseText) as T;
    } catch (error) {
      throw new Error('Invalid JSON response from chat backend');
    }
  }

  // Conversations
  async getConversations(params?: {
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<ConversationsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const endpoint = `/api/conversations${query ? `?${query}` : ''}`;

    return this.makeRequest<ConversationsResponse>(endpoint);
  }

  async createConversation(data: CreateConversationRequest): Promise<ChatConversation> {
    return this.makeRequest<ChatConversation>('/api/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getConversation(id: string): Promise<ChatConversation> {
    return this.makeRequest<ChatConversation>(`/api/conversations/${id}`);
  }

  async updateConversation(id: string, data: { title: string }): Promise<ChatConversation> {
    return this.makeRequest<ChatConversation>(`/api/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteConversation(id: string): Promise<void> {
    await this.makeRequest<void>(`/api/conversations/${id}`, {
      method: 'DELETE',
    });
  }

  // Messages
  async getMessages(conversationId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<MessagesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const endpoint = `/api/conversations/${conversationId}/messages${query ? `?${query}` : ''}`;

    return this.makeRequest<MessagesResponse>(endpoint);
  }

  async addMessage(conversationId: string, data: CreateMessageRequest): Promise<ChatMessage> {
    return this.makeRequest<ChatMessage>(`/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Utilité pour générer un titre automatique depuis le premier message
  generateConversationTitle(firstMessage: string, maxLength: number = 50): string {
    // Nettoyer le message
    let title = firstMessage
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Tronquer si nécessaire
    if (title.length > maxLength) {
      title = title.substring(0, maxLength - 3) + '...';
    }

    // Si le titre est vide ou trop court, utiliser un titre par défaut
    if (title.length < 3) {
      title = 'Nouvelle conversation';
    }

    return title;
  }

  // Test de connexion
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      return await this.makeRequest<{ status: string; timestamp: string }>('/health');
    } catch (error) {
      throw new Error(`Chat backend health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Instance singleton
export const chatHistoryService = new ChatHistoryService();

// Hook personnalisé pour React (optionnel)
export const useChatHistory = () => {
  return {
    service: chatHistoryService,

    // Méthodes utilitaires
    generateTitle: chatHistoryService.generateConversationTitle,

    // Vérification de disponibilité
    isAvailable: () => {
      try {
        const { idToken, getChatBackendUrl } = useTenantStore.getState();
        return !!idToken && !!getChatBackendUrl();
      } catch {
        return false;
      }
    }
  };
};